import { call, put, select, takeEvery } from "redux-saga/effects";

import { PinnedFieldsVisibility } from "@mapmycustomers/shared";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { EntityType } from "@mapmycustomers/shared/types/entity";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUser,
  getOrganization,
  getOrganizationId,
  getOrganizationMetaData,
  getSourceObjectValues,
  isCurrentUserOwner,
} from "@app/store/iam";
import { updateMetadata, updateOrganizationMetadata } from "@app/store/iam/actions";
import { getTeams } from "@app/store/members";
import { fetchTeams, updateTeam } from "@app/store/members/actions";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import { callApi } from "../api/callApi";

import { setRecordsPreviewConfiguration, updateRecordsPreviewConfiguration } from "./actions";
import EntityColumnsConfiguration from "./EntityColumnsConfiguration";
import { getRecordPreviewConfiguration } from "./selectors";
import applyPinnedListsChange from "./util/applyPinnedListsChange";
import buildEntityColumnsConfiguration from "./util/buildEntityColumnsConfiguration";
import getChangedPinnedLists from "./util/getChangedPinnedLists";
import getLevelPinnedLists from "./util/getLevelPinnedLists";
import toMapConfigurationPatch from "./util/toMapConfigurationPatch";

export function* onUpdateRecordsPreviewConfiguration({
  payload,
}: ReturnType<typeof updateRecordsPreviewConfiguration.request>) {
  try {
    const owner: boolean = yield select(isCurrentUserOwner);
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const configuration: EntityColumnsConfiguration = yield select(getRecordPreviewConfiguration);

    // A list or flag is compared only when both the store and the payload hold it, so one the
    // payload leaves out is never written
    const getLevelChange = (visibility: PinnedFieldsVisibility, teamId?: Team["id"]) =>
      getChangedPinnedLists(
        getLevelPinnedLists(configuration, visibility, teamId),
        getLevelPinnedLists(payload.configuration, visibility, teamId)
      );

    if (owner) {
      const orgChange = getLevelChange(Visibility.SHARED_WITH_ORGANIZATION);

      if (orgChange) {
        const organization: Organization = yield select(getOrganization);
        // The org PUT replaces the whole metaData, so the cached copy goes back with every key
        const organizationUpdatePayload: Pick<Organization, "id" | "metaData"> = {
          id: organization.id,
          metaData: applyPinnedListsChange(organization.metaData, orgChange),
        };
        const updatedOrganization: Organization = yield callApi(
          "updateOrganization",
          organizationUpdatePayload
        );

        yield put(updateOrganizationMetadata.success(updatedOrganization));
      }

      const teams: Team[] = yield select(getTeams);
      const teamChanges = teams.flatMap((team) => {
        const change = getLevelChange(Visibility.SHARED_WITH_TEAM, team.id);
        return change ? [{ change, team }] : [];
      });

      for (const { change, team } of teamChanges) {
        // The team PUT replaces the whole metaData, so the cached copy goes back with every key
        const response: Team = yield callApi("updateTeam", orgId, {
          ...team,
          metaData: applyPinnedListsChange(team.metaData, change),
        });
        // Platform doesn't return this information in PUT response, so we need to copy it manually,
        // otherwise just visible team becomes invisible to user
        yield put(updateTeam.success({ ...response, viaTeamAccess: team.viaTeamAccess }));
      }

      if (teamChanges.length) {
        yield put(fetchTeams.request());
      }
    }

    const userChange = getLevelChange(Visibility.PRIVATE);

    if (userChange) {
      const user: User = yield select(getCurrentUser);
      const updatedUser: User = yield callApi("patchMeMetaData", user.id, {
        mapConfiguration: toMapConfigurationPatch(userChange),
      });
      yield put(updateMetadata.success(updatedUser.metaData));
    }

    // Update list configuration to reflect changes on display
    yield call(buildRecordsPreviewConfiguration);

    if (payload.successCallback) {
      yield call(payload.successCallback);
    }
    yield put(updateRecordsPreviewConfiguration.success());
  } catch (error) {
    // No rebuild here, so the unsaved edits stay in the draft for another try
    yield put(updateRecordsPreviewConfiguration.failure());
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* buildRecordsPreviewConfiguration() {
  try {
    const orgMetadata: OrganizationMetaData = yield select(getOrganizationMetaData);
    const currentUser: User = yield select(getCurrentUser);
    const teams: Team[] = yield select(getTeams);
    const owner: boolean = yield select(isCurrentUserOwner);
    const companyValues: SourceObjectValue[] = yield select(
      getSourceObjectValues,
      EntityType.COMPANY
    );
    const personValues: SourceObjectValue[] = yield select(
      getSourceObjectValues,
      EntityType.PERSON
    );

    const configuration: EntityColumnsConfiguration = yield call(buildEntityColumnsConfiguration, {
      isOwner: owner,
      org: orgMetadata?.mapConfiguration,
      resolveField: (entityType, fieldName) =>
        getFieldModelByEntityType(entityType).getByName(fieldName),
      sourceObjectValues: {
        [EntityType.COMPANY]: companyValues,
        [EntityType.PERSON]: personValues,
      },
      teams,
      user: currentUser?.metaData?.mapConfiguration,
    });

    yield put(setRecordsPreviewConfiguration(configuration));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* recordPreviewSaga() {
  yield takeEvery(updateRecordsPreviewConfiguration.request, onUpdateRecordsPreviewConfiguration);
}
